import React, { useState, useEffect, useCallback } from 'react';
import { TextField } from '@mui/material';

// Debounce-Funktion
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

interface LocalTextFieldProps {
  label: string;
  initialValue: string;
  anforderungNummer: number;
  field: string;
  disabled: boolean;
  multiline?: boolean;
  rows?: number;
  sx?: any;
  onLocalChange: (anforderungNummer: number, field: string, value: string) => void;
}

// Lokaler TextField mit eigenem State (keine Parent-Re-Renders) - mit Label im Border wie LocalTextbelegeField
const LocalTextField = React.memo<LocalTextFieldProps>(({ 
  label, 
  initialValue, 
  anforderungNummer, 
  field, 
  disabled, 
  multiline = false, 
  rows = 1, 
  sx = {}, 
  onLocalChange 
}) => {
  const [localValue, setLocalValue] = useState(initialValue);
  
  // Update lokaler Wert wenn sich initialValue ändert (z.B. nach Reset)
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Debounced Change für Parent-Update (optional für Live-Vorschau)
  const debouncedChange = useCallback(
    debounce((value: string) => {
      onLocalChange(anforderungNummer, field, value);
    }, 300), // 300ms Verzögerung wie bei LocalTextbelegeField
    [anforderungNummer, field, onLocalChange]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Sofortiges lokales Update
    debouncedChange(newValue); // Verzögertes Parent-Update
  }, [debouncedChange]);

  return (
    <TextField
      fullWidth
      multiline={multiline}
      {...(multiline ? { minRows: rows } : {})}
      label={label}
      value={localValue}
      disabled={disabled}
      onChange={handleChange}
      sx={{ 
        mb: 1, 
        ...sx,
        '& .MuiInputBase-input': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' },
        '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' }
      }}
      inputProps={{ style: { fontSize: '0.875rem' } }}
    />
  );
});

LocalTextField.displayName = 'LocalTextField';

export default LocalTextField;